<template>
  <section class="relative isolate min-h-[calc(100vh-8rem)] overflow-hidden py-8">
    <div class="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div class="grid h-full w-full grid-cols-3 gap-3 px-3 opacity-95 sm:grid-cols-4 sm:gap-4 sm:px-4 lg:grid-cols-6">
        <div
          v-for="(column, columnIndex) in imageColumns"
          :key="columnIndex"
          class="auth-masonry-column"
          :class="{ 'hidden lg:grid': columnIndex > 3 }"
        >
          <img
            v-for="card in column"
            :key="card.src"
            class="h-full min-h-[9rem] w-full rounded-2xl object-cover shadow-xl shadow-stone-900/10"
            :src="card.src"
            :alt="card.alt"
            loading="lazy"
          />
        </div>
      </div>
      <div class="absolute inset-0 bg-gradient-to-b from-[#fdf7f0]/20 via-[#fdf7f0]/55 to-[#fdf7f0]" />
      <div class="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#fdf7f0] to-transparent" />
    </div>

    <div class="mx-auto flex min-h-[calc(100vh-12rem)] w-full max-w-6xl items-center justify-center px-4 sm:px-6 lg:px-8">
      <div class="grid w-full max-w-5xl items-center gap-8 lg:grid-cols-[1fr_26rem]">
        <div class="hidden max-w-xl text-slate-950 lg:block">
          <p class="mb-4 text-sm font-black uppercase tracking-[0.22em] text-rose-700">Create with NetFlyr</p>
          <h1 class="m-0 text-5xl font-black leading-[0.95]">
            Put your flyers where local people can find them.
          </h1>
          <p class="mt-5 max-w-lg text-lg leading-8 text-slate-700">
            Start a free account to publish promotions, save inspiration, and keep your neighborhood
            updates organized in one friendly place.
          </p>
          <div class="mt-8 grid max-w-lg grid-cols-3 gap-3">
            <div class="rounded-lg bg-white/80 p-4 shadow-lg shadow-stone-900/10 backdrop-blur">
              <strong class="block text-2xl font-black text-rose-700">Post</strong>
              <span class="text-sm font-bold text-slate-600">events and offers</span>
            </div>
            <div class="rounded-lg bg-white/80 p-4 shadow-lg shadow-stone-900/10 backdrop-blur">
              <strong class="block text-2xl font-black text-blue-700">Share</strong>
              <span class="text-sm font-bold text-slate-600">local updates</span>
            </div>
            <div class="rounded-lg bg-white/80 p-4 shadow-lg shadow-stone-900/10 backdrop-blur">
              <strong class="block text-2xl font-black text-emerald-700">Grow</strong>
              <span class="text-sm font-bold text-slate-600">your reach</span>
            </div>
          </div>
        </div>

        <form
          class="mx-auto w-full max-w-[26rem] rounded-[2rem] border border-white/80 bg-white/95 p-6 shadow-2xl shadow-stone-900/20 backdrop-blur sm:p-8"
          @submit.prevent="register"
        >
          <div class="mb-7 text-center">
            <img class="mx-auto mb-4 h-12 w-auto" src="/logo.svg" alt="NetFlyr" />
            <h2 class="m-0 text-3xl font-black leading-tight text-slate-950">Join NetFlyr</h2>
            <p class="mx-auto mt-2 max-w-xs text-sm leading-6 text-slate-600">
              Create your account to publish flyers, save favorites, and explore local discoveries.
            </p>
          </div>

          <div class="grid gap-4">
            <div v-if="!selectedUserType" class="grid gap-3">
              <button
                v-for="type in userTypes"
                :key="type.value"
                class="flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left transition hover:border-blue-200 hover:bg-blue-50/40 focus:outline-none focus:ring-4 focus:ring-blue-100"
                :class="selectedUserType === type.value ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-slate-200'"
                type="button"
                :aria-pressed="selectedUserType === type.value"
                @click="selectUserType(type.value)"
              >
                <span
                  class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                  :class="type.iconClass"
                  aria-hidden="true"
                >
                  <svg
                    v-if="type.value === 'business'"
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    <rect width="20" height="14" x="2" y="6" rx="2" />
                  </svg>
                  <svg
                    v-else
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block text-base font-black leading-5 text-slate-950">{{ type.label }}</span>
                  <span class="mt-1 block text-sm font-semibold leading-5 text-slate-500">{{ type.description }}</span>
                </span>
                <span class="text-xl font-bold text-slate-300" aria-hidden="true">›</span>
              </button>
            </div>

            <template v-else>
            <div class="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div class="min-w-0">
                <span class="block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">Account type</span>
                <span class="block truncate text-sm font-medium text-slate-800">{{ selectedUserTypeDetails.label }}</span>
              </div>
              <button
                class="shrink-0 text-sm font-medium text-blue-700 transition hover:text-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-100"
                type="button"
                @click="changeUserType"
              >
                Change
              </button>
            </div>

            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              <span>{{ nameLabel }}</span>
              <input
                v-model="name"
                class="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 font-normal text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
                autocomplete="name"
                :placeholder="namePlaceholder"
                required
              />
            </label>

            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              <span>Email address</span>
              <input
                v-model="email"
                class="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 font-normal text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
                type="email"
                autocomplete="email"
                placeholder="you@example.com"
                required
              />
            </label>

            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              <span>Username</span>
              <input
                v-model="username"
                class="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 font-normal text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
                type="text"
                autocomplete="username"
                placeholder="@username"
                required
              />
            </label>

            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              <span>Password</span>
              <div class="relative">
                <input
                  v-model="password"
                  class="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 pr-20 font-normal text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  placeholder="Create a password"
                  required
                />
                <button
                  class="absolute right-11 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-4 focus:ring-rose-100"
                  type="button"
                  aria-label="Generate strong password"
                  @click="generatePassword"
                >
                  <svg
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M15 4V2" />
                    <path d="M15 16v-2" />
                    <path d="M8 9h2" />
                    <path d="M20 9h2" />
                    <path d="m17.8 6.2 1.4-1.4" />
                    <path d="m12.2 11.8-1.4 1.4" />
                    <path d="m17.8 11.8 1.4 1.4" />
                    <path d="m12.2 6.2-1.4-1.4" />
                    <path d="M3 21l9-9" />
                    <path d="M12 12 9 9" />
                  </svg>
                </button>
                <button
                  class="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-4 focus:ring-rose-100"
                  type="button"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  :aria-pressed="showPassword"
                  @click="showPassword = !showPassword"
                >
                  <svg
                    v-if="showPassword"
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M10.7 5.1A10.9 10.9 0 0 1 12 5c5 0 9 4.5 10 7a13.5 13.5 0 0 1-3.2 4.5" />
                    <path d="M6.6 6.6A13 13 0 0 0 2 12c1 2.5 5 7 10 7a10.9 10.9 0 0 0 5.4-1.5" />
                    <path d="M2 2l20 20" />
                    <path d="M9.5 9.5a3.5 3.5 0 0 0 5 5" />
                    <path d="M14.5 9.5a3.5 3.5 0 0 0-5 5" />
                  </svg>
                  <svg
                    v-else
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
            </label>

            <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div class="flex items-center justify-between gap-3 text-xs font-black uppercase tracking-[0.16em] text-slate-500">
                <span>Password strength</span>
                <span :class="strengthLabelClass">{{ strengthLabel }}</span>
              </div>
              <div class="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                <div
                  class="h-full rounded-full transition-all"
                  :class="strengthBarClass"
                  :style="{ width: strengthPercent + '%' }"
                />
              </div>
              <ul class="mt-3 grid gap-2 text-xs font-bold leading-5 text-slate-600">
                <li
                  v-for="item in passwordChecklist"
                  :key="item.label"
                  class="flex items-center gap-2"
                  :class="item.valid ? 'text-emerald-700' : 'text-slate-500'"
                >
                  <span
                    class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[0.65rem] font-black"
                    :class="item.valid ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-slate-400'"
                  >
                    <svg
                      v-if="item.valid"
                      class="h-3.5 w-3.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.071 7.132a1 1 0 0 1-1.42.002l-3.182-3.2a1 1 0 1 1 1.418-1.41l2.472 2.486 6.363-6.418a1 1 0 0 1 1.414-.006Z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <span v-else>-</span>
                  </span>
                  {{ item.label }}
                </li>
              </ul>
            </div>

            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              <span>Confirm password</span>
              <div class="relative">
                <input
                  v-model="confirmPassword"
                  class="min-h-12 w-full rounded-2xl border bg-white px-4 py-3 pr-12 font-normal text-slate-950 outline-none transition placeholder:text-slate-400 focus:ring-4"
                  :class="confirmPassword && !passwordsMatch ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-300 focus:border-rose-500 focus:ring-rose-100'"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  placeholder="Re-enter your password"
                  required
                />
                <button
                  class="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-4 focus:ring-rose-100"
                  type="button"
                  :aria-label="showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'"
                  :aria-pressed="showConfirmPassword"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <svg
                    v-if="showConfirmPassword"
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M10.7 5.1A10.9 10.9 0 0 1 12 5c5 0 9 4.5 10 7a13.5 13.5 0 0 1-3.2 4.5" />
                    <path d="M6.6 6.6A13 13 0 0 0 2 12c1 2.5 5 7 10 7a10.9 10.9 0 0 0 5.4-1.5" />
                    <path d="M2 2l20 20" />
                    <path d="M9.5 9.5a3.5 3.5 0 0 0 5 5" />
                    <path d="M14.5 9.5a3.5 3.5 0 0 0-5 5" />
                  </svg>
                  <svg
                    v-else
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
              <span
                v-if="confirmPassword"
                class="text-xs font-bold"
                :class="passwordsMatch ? 'text-emerald-700' : 'text-red-600'"
              >
                {{ passwordsMatch ? 'Passwords match.' : 'Passwords do not match yet.' }}
              </span>
            </label>

            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              <span>Referral code <span class="font-normal text-slate-400">(optional)</span></span>
              <input
                v-model="referralCode"
                class="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 font-normal text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
                type="text"
                autocomplete="off"
                placeholder="Enter referral code"
              />
            </label>
            </template>
          </div>

          <p class="mt-4 text-xs font-bold leading-5 text-slate-500">
            By creating an account, you agree to keep your flyers accurate, helpful, and easy for local audiences to understand.
          </p>

          <div
            v-if="formError"
            class="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold leading-6 text-red-700"
          >
            {{ formError }}
          </div>

          <FlyerButton
            v-if="selectedUserType"
            class="mt-5 w-full rounded-full bg-blue-600 hover:bg-blue-700 focus:ring-blue-200"
            type="submit"
            :disabled="isSubmitting"
          >
            {{ isRegisterSubmitting ? 'Creating account...' : 'Create account' }}
          </FlyerButton>

          <div v-if="selectedUserType" class="mt-4">
            <div class="flex items-center gap-3">
              <span class="h-px flex-1 bg-slate-200"></span>
              <span class="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">or</span>
              <span class="h-px flex-1 bg-slate-200"></span>
            </div>

            <button
              class="mt-4 flex min-h-11 w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-5 text-sm font-medium text-slate-800 transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
              type="button"
              :disabled="isSubmitting"
              @click="continueWithGoogle"
            >
              <img class="h-5 w-5" src="@/assets/google-icon.png" alt="" />
              {{ isGoogleSubmitting ? 'Connecting Google...' : 'Continue with Google' }}
            </button>
          </div>

          <p class="mt-6 text-center text-sm font-bold leading-6 text-slate-600">
            Already have an account?
            <router-link class="font-black text-blue-700 hover:text-blue-800" to="/login">
              Log in
            </router-link>
          </p>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth'
import Swal from 'sweetalert2'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import { auth } from '@/services/firebase'
import FlyerButton from '@/components/common/FlyerButton.vue'

const router = useRouter()
const route = useRoute()
const session = useSessionStore()
const LOCATION_STORAGE_KEY = 'netflyr.location'

const COUNTRY_DEFAULTS = {
  India: { weight: 'kg', miles: 'kms', currency: 'rupee' },
  'United States': { weight: 'lbs', miles: 'miles', currency: 'usd' },
  Canada: { weight: 'lbs', miles: 'kms', currency: 'cad' },
  'United Kingdom': { weight: 'kg', miles: 'miles', currency: 'gbp' }
}

const name = ref('')
const email = ref('')
const username = ref('')
const password = ref('')
const confirmPassword = ref('')
const referralCode = ref('')
const selectedUserType = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const formError = ref('')
const isRegisterSubmitting = ref(false)
const isGoogleSubmitting = ref(false)
const isSubmitting = computed(() => isRegisterSubmitting.value || isGoogleSubmitting.value)

const userTypes = [
  {
    value: 'business',
    label: 'Business',
    description: 'Post offers, events, jobs & more to your neighborhood',
    iconClass: 'bg-teal-50 text-teal-600'
  },
  {
    value: 'user',
    label: 'Community Member',
    description: "Discover local deals, share reviews, confirm what's real",
    iconClass: 'bg-orange-50 text-orange-600'
  }
]

const isBusinessAccount = computed(() => selectedUserType.value === 'business')
const selectedUserTypeDetails = computed(() => userTypes.find((type) => type.value === selectedUserType.value) || userTypes[1])
const nameLabel = computed(() => (isBusinessAccount.value ? 'Business name' : 'Full name'))
const namePlaceholder = computed(() => (isBusinessAccount.value ? 'Your business name' : 'Your name'))

const passwordChecks = computed(() => ({
  length: password.value.length >= 12,
  uppercase: /[A-Z]/.test(password.value),
  lowercase: /[a-z]/.test(password.value),
  number: /\d/.test(password.value),
  symbol: /[^A-Za-z0-9]/.test(password.value)
}))

const passwordChecklist = computed(() => [
  { label: 'Use at least 12 characters', valid: passwordChecks.value.length },
  { label: 'Include uppercase and lowercase letters', valid: passwordChecks.value.uppercase && passwordChecks.value.lowercase },
  { label: 'Add at least one number', valid: passwordChecks.value.number },
  { label: 'Add at least one symbol', valid: passwordChecks.value.symbol }
])

const strengthScore = computed(() => Object.values(passwordChecks.value).filter(Boolean).length)
const strengthPercent = computed(() => (strengthScore.value / 5) * 100)
const passwordsMatch = computed(() => Boolean(password.value && confirmPassword.value && password.value === confirmPassword.value))

const strengthLabel = computed(() => {
  if (!password.value) return 'Start'
  if (strengthScore.value <= 2) return 'Weak'
  if (strengthScore.value <= 4) return 'Good'
  return 'Strong'
})

const strengthBarClass = computed(() => {
  if (!password.value) return 'bg-slate-300'
  if (strengthScore.value <= 2) return 'bg-red-500'
  if (strengthScore.value <= 4) return 'bg-amber-500'
  return 'bg-emerald-500'
})

const strengthLabelClass = computed(() => {
  if (!password.value) return 'text-slate-500'
  if (strengthScore.value <= 2) return 'text-red-600'
  if (strengthScore.value <= 4) return 'text-amber-600'
  return 'text-emerald-700'
})

const inferCurrentCountry = () => {
  const locale = navigator.language || ''
  const region = locale.includes('-') ? locale.split('-').pop() : ''

  if (region && typeof Intl !== 'undefined' && Intl.DisplayNames) {
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' })
    const resolved = displayNames.of(region)
    if (resolved && COUNTRY_DEFAULTS[resolved]) return resolved
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  if (timeZone.includes('Kolkata')) return 'India'
  if (timeZone.includes('London')) return 'United Kingdom'
  if (timeZone.includes('Toronto') || timeZone.includes('Vancouver')) return 'Canada'
  return 'United States'
}

const getSavedCountry = () => {
  const saved = localStorage.getItem(LOCATION_STORAGE_KEY)
  if (saved) {
    try {
      const parsed = JSON.parse(saved)
      const location = parsed?.location?.toLowerCase?.() || ''

      if (location.includes('india')) return 'India'
      if (location.includes('united kingdom') || location.includes('uk')) return 'United Kingdom'
      if (location.includes('canada')) return 'Canada'
      if (location.includes('united states') || location.includes('usa')) return 'United States'
    } catch (error) {
      localStorage.removeItem(LOCATION_STORAGE_KEY)
    }
  }

  return inferCurrentCountry()
}

const countrySettings = computed(() => COUNTRY_DEFAULTS[getSavedCountry()] || COUNTRY_DEFAULTS['United States'])

const getTimezonePayload = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  const offsetMinutes = -new Date().getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absoluteMinutes = Math.abs(offsetMinutes)
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, '0')
  const minutes = String(absoluteMinutes % 60).padStart(2, '0')

  return {
    timezone,
    zone: `GMT${sign}${hours}:${minutes}`
  }
}

const firstValue = (...values) => values.find((value) => String(value ?? '').trim() !== '')

const normalizeUserType = (value) => String(value ?? '').trim().toLowerCase()

const extractUserType = (payload = {}) => {
  const source = Array.isArray(payload?.data) ? payload.data[0] || {} : payload?.data || payload || {}
  return normalizeUserType(firstValue(source.user_type, source.userType, payload.user_type, payload.userType, ''))
}

const needsUserTypeChoice = (userType) => !userType || userType === 'pending'

const apiLoginSucceeded = (payload) =>
  payload?.status === 'ok' && (payload?.tempstatus === undefined || String(payload.tempstatus) === '0') && !!payload?.data?.[0]

const getApiMessage = (payload, fallback) =>
  payload?.msg || payload?.errors?.[0]?.msg || payload?.message || fallback

const showRegisterToast = (icon, title) =>
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon,
    title,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  })

const extractUser = (payload, fallback = {}) => {
  const rawUser = payload?.data?.[0] || payload?.user || payload?.data || {}

  return {
    ...rawUser,
    id: rawUser.id || rawUser.user_id || rawUser.userid || rawUser.uid || Date.now(),
    name: rawUser.name || rawUser.user_name || fallback.name || name.value.trim() || 'NetFlyr User',
    username: rawUser.username || rawUser.user_name || fallback.username || username.value.trim(),
    email: rawUser.email || fallback.email || email.value.trim()
  }
}

const imageColumns = [
  [
    { src: 'https://dummyimage.com/360x520/f43f5e/ffffff&text=Launch+Flyer', alt: 'Colorful NetFlyr placeholder for a launch flyer' },
    { src: 'https://dummyimage.com/360x430/0ea5e9/ffffff&text=Open+Mic', alt: 'Colorful NetFlyr placeholder for an open mic flyer' },
    { src: 'https://dummyimage.com/360x480/22c55e/ffffff&text=Weekend+Market', alt: 'Colorful NetFlyr placeholder for a weekend market flyer' }
  ],
  [
    { src: 'https://dummyimage.com/360x420/f59e0b/ffffff&text=Street+Food', alt: 'Colorful NetFlyr placeholder for a street food flyer' },
    { src: 'https://dummyimage.com/360x560/16a34a/ffffff&text=Home+Services', alt: 'Colorful NetFlyr placeholder for home service flyers' },
    { src: 'https://dummyimage.com/360x450/db2777/ffffff&text=Pop-up+Shop', alt: 'Colorful NetFlyr placeholder for a pop-up shop flyer' }
  ],
  [
    { src: 'https://dummyimage.com/360x590/8b5cf6/ffffff&text=Skill+Class', alt: 'Colorful NetFlyr placeholder for a skill class flyer' },
    { src: 'https://dummyimage.com/360x400/ec4899/ffffff&text=Flash+Deal', alt: 'Colorful NetFlyr placeholder for a flash deal flyer' },
    { src: 'https://dummyimage.com/360x500/0f766e/ffffff&text=Art+Walk', alt: 'Colorful NetFlyr placeholder for an art walk flyer' }
  ],
  [
    { src: 'https://dummyimage.com/360x470/14b8a6/ffffff&text=Local+Club', alt: 'Colorful NetFlyr placeholder for a local club flyer' },
    { src: 'https://dummyimage.com/360x540/ef4444/ffffff&text=New+Store', alt: 'Colorful NetFlyr placeholder for a new store flyer' },
    { src: 'https://dummyimage.com/360x430/4f46e5/ffffff&text=Music+Night', alt: 'Colorful NetFlyr placeholder for a music night flyer' }
  ],
  [
    { src: 'https://dummyimage.com/360x530/2563eb/ffffff&text=Yoga+Batch', alt: 'Colorful NetFlyr placeholder for a yoga batch flyer' },
    { src: 'https://dummyimage.com/360x410/a855f7/ffffff&text=Craft+Fair', alt: 'Colorful NetFlyr placeholder for a craft fair flyer' },
    { src: 'https://dummyimage.com/360x560/e11d48/ffffff&text=Book+Swap', alt: 'Colorful NetFlyr placeholder for a book swap flyer' }
  ],
  [
    { src: 'https://dummyimage.com/360x430/f97316/ffffff&text=Now+Hiring', alt: 'Colorful NetFlyr placeholder for a hiring flyer' },
    { src: 'https://dummyimage.com/360x570/0891b2/ffffff&text=City+Updates', alt: 'Colorful NetFlyr placeholder for city updates' },
    { src: 'https://dummyimage.com/360x460/65a30d/ffffff&text=Pet+Clinic', alt: 'Colorful NetFlyr placeholder for a pet clinic flyer' }
  ]
]

const generatePassword = () => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lower = 'abcdefghijkmnopqrstuvwxyz'
  const numbers = '23456789'
  const symbols = '!@#$%^&*?'
  const groups = [upper, lower, numbers, symbols]
  const allCharacters = groups.join('')
  const generated = [
    ...groups.map((group) => group[Math.floor(Math.random() * group.length)]),
    ...Array.from({ length: 12 }, () => allCharacters[Math.floor(Math.random() * allCharacters.length)])
  ]
    .sort(() => Math.random() - 0.5)
    .join('')

  password.value = generated
  confirmPassword.value = generated
  showPassword.value = true
  showConfirmPassword.value = true
  formError.value = ''
}

const selectUserType = (type) => {
  selectedUserType.value = type
  formError.value = ''
}

const changeUserType = () => {
  selectedUserType.value = ''
  formError.value = ''
}

const register = async () => {
  if (isSubmitting.value) return

  formError.value = ''

  if (!selectedUserType.value) {
    formError.value = 'Choose an account type to continue.'
    return
  }

  if (!passwordsMatch.value) {
    formError.value = 'Confirm password must match your password.'
    return
  }

  if (strengthScore.value < 5) {
    formError.value = 'Please use a strong password before creating your account.'
    return
  }

  isRegisterSubmitting.value = true

  try {
    const { data } = await api.register({
      name: name.value.trim(),
      phone_code: '',
      phone: '',
      email: email.value.trim(),
      username: username.value.trim(),
      user_type: selectedUserType.value,
      password: password.value,
      ...getTimezonePayload(),
      weight: countrySettings.value.weight,
      miles: countrySettings.value.miles,
      currency: countrySettings.value.currency,
      referral_code: referralCode.value.trim()
    })

    session.setUser(extractUser(data))
    router.push(typeof route.query.redirect === 'string' ? route.query.redirect : '/explore')
  } catch (error) {
    formError.value = error?.response?.data?.message || error?.message || 'Registration failed. Please try again.'
  } finally {
    isRegisterSubmitting.value = false
  }
}

const continueWithGoogle = async () => {
  if (isSubmitting.value) return

  formError.value = ''

  if (!selectedUserType.value) {
    formError.value = 'Choose an account type to continue with Google.'
    return
  }

  isGoogleSubmitting.value = true

  try {
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })

    const result = await signInWithPopup(auth, provider)
    const googleUser = result.user
    const googleEmail = googleUser.email?.trim() || ''
    const googleName = googleUser.displayName?.trim() || googleEmail.split('@')[0] || 'Google User'
    const { data: checkData } = await api.socialLoginCheck({ email: googleEmail })
    const checkedUserType = extractUserType(checkData)
    const userType = needsUserTypeChoice(checkedUserType) ? selectedUserType.value : checkedUserType

    const { data } = await api.socialLogin({
      name: googleName,
      email: googleEmail,
      ...getTimezonePayload(),
      login_type: 'google',
      user_type: userType
    })

    if (!apiLoginSucceeded(data)) {
      const message = getApiMessage(data, 'Google registration failed.')
      formError.value = message
      await showRegisterToast('error', message)
      return
    }

    session.setUser(extractUser(data, {
      name: googleName,
      username: googleEmail.split('@')[0] || googleName,
      email: googleEmail
    }))
    await showRegisterToast('success', getApiMessage(data, 'Login successful'))
    router.push(typeof route.query.redirect === 'string' ? route.query.redirect : '/explore')
  } catch (error) {
    if (error?.code === 'auth/popup-closed-by-user') {
      formError.value = 'Google login was cancelled.'
      await showRegisterToast('info', formError.value)
      return
    }

    const message = getApiMessage(error?.response?.data, error?.message || 'Google registration failed.')
    formError.value = message
    await showRegisterToast('error', message)
  } finally {
    isGoogleSubmitting.value = false
  }
}
</script>

<style scoped>
.auth-masonry-column {
  display: grid;
  min-height: 100%;
  grid-template-rows: 1.1fr 0.9fr 1fr;
  gap: 0.75rem;
}

.auth-masonry-column:nth-child(even) {
  transform: translateY(2rem);
}

.auth-masonry-column:nth-child(3n) {
  transform: translateY(-1.5rem);
}

@media (min-width: 640px) {
  .auth-masonry-column {
    gap: 1rem;
  }
}

@media (max-width: 1023px) {
  .auth-masonry-column:nth-child(even),
  .auth-masonry-column:nth-child(3n) {
    transform: none;
  }
}
</style>
